import { createContext, useCallback, useContext, useRef } from "react";
import type { ReactNode } from "react";

interface Zoom {
  scale: number;
  positionX: number;
  positionY: number;
}

interface ZoomableImageContextValue {
  recordZoomForImageIndex: (index: number, zoom: Zoom) => void;
  getRecordedZoomForImageIndex: (index: number) => Zoom | undefined;
}

const ZoomableImageContext = createContext<ZoomableImageContextValue | null>(
  null,
);

export const ZoomableImageProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const zoomByIndexRef = useRef<Record<number, Zoom>>({});

  const recordZoomForImageIndex = useCallback((index: number, zoom: Zoom) => {
    zoomByIndexRef.current[index] = zoom;
  }, []);

  const getRecordedZoomForImageIndex = useCallback(
    (index: number) => zoomByIndexRef.current[index],
    [],
  );

  return (
    <ZoomableImageContext.Provider
      value={{ recordZoomForImageIndex, getRecordedZoomForImageIndex }}
    >
      {children}
    </ZoomableImageContext.Provider>
  );
};

export const useZoomableImageContext = (): ZoomableImageContextValue => {
  const context = useContext(ZoomableImageContext);

  if (!context) {
    throw new Error(
      "useZoomableImageContext must be used within a ZoomableImageProvider",
    );
  }

  return context;
};
